/**
 * Micro-interactions and Advanced Features
 * Modern, fun interactive elements for the golf accessories store
 */

// ===== TOAST NOTIFICATION SYSTEM =====
class ToastNotification {
  constructor() {
    this.container = null;
    this.init();
  }

  init() {
    // Create toast container if it doesn't exist
    if (!document.getElementById('toast-container')) {
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    } else {
      this.container = document.getElementById('toast-container');
    }
  }

  show(message, type = 'success', duration = 3000) {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    // Icon based on type
    const icons = {
      success: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <path d="M20 6L9 17l-5-5"/>
      </svg>`,
      error: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M15 9l-6 6M9 9l6 6"/>
      </svg>`,
      info: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 16v-4M12 8h.01"/>
      </svg>`,
      warning: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <path d="M12 9v4M12 17h.01"/>
      </svg>`
    };

    toast.innerHTML = `
      <div class="toast-icon">${icons[type] || icons.success}</div>
      <div class="toast-message">${message}</div>
      <button class="toast-close" aria-label="Close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    `;

    this.container.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('toast-show');
    });

    // Close button
    const closeBtn = toast.querySelector('.toast-close');
    closeBtn.addEventListener('click', () => this.hide(toast));

    // Auto hide
    setTimeout(() => this.hide(toast), duration);
  }

  hide(toast) {
    toast.classList.remove('toast-show');
    toast.classList.add('toast-hide');
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }
}

// Initialize toast system
const toast = new ToastNotification();
window.showToast = (message, type, duration) => toast.show(message, type, duration);


// ===== CONFETTI ANIMATION =====
class ConfettiAnimation {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.particles = [];
    this.animationId = null;
  }

  init() {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.id = 'confetti-canvas';
      this.canvas.style.position = 'fixed';
      this.canvas.style.top = '0';
      this.canvas.style.left = '0';
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';
      this.canvas.style.pointerEvents = 'none';
      this.canvas.style.zIndex = '9999';
      document.body.appendChild(this.canvas);

      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();

      window.addEventListener('resize', () => this.resizeCanvas());
    }
  }

  resizeCanvas() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  createParticle(x, y) {
    const colors = ['#D7FF01', '#F4FF80', '#F97316', '#FDBA74', '#0EA5E9', '#7DD3FC'];
    return {
      x: x,
      y: y,
      vx: (Math.random() - 0.5) * 8,
      vy: Math.random() * -10 - 5,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      size: Math.random() * 8 + 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      gravity: 0.3,
      life: 1,
      decay: Math.random() * 0.015 + 0.01
    };
  }

  burst(x, y, count = 50) {
    this.init();

    // Reduce particle count on mobile for better performance
    const isMobile = window.innerWidth <= 768;
    const particleCount = isMobile ? Math.floor(count / 2) : count;

    for (let i = 0; i < particleCount; i++) {
      this.particles.push(this.createParticle(x, y));
    }

    if (!this.animationId) {
      this.animate();
    }
  }

  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.particles = this.particles.filter(p => {
      // Update
      p.vy += p.gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.rotationSpeed;
      p.life -= p.decay;

      // Draw
      if (p.life > 0) {
        this.ctx.save();
        this.ctx.translate(p.x, p.y);
        this.ctx.rotate((p.rotation * Math.PI) / 180);
        this.ctx.globalAlpha = p.life;
        this.ctx.fillStyle = p.color;
        this.ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        this.ctx.restore();
        return true;
      }
      return false;
    });

    if (this.particles.length > 0) {
      this.animationId = requestAnimationFrame(() => this.animate());
    } else {
      this.animationId = null;
    }
  }
}

const confetti = new ConfettiAnimation();
window.confettiBurst = (x, y, count) => confetti.burst(x, y, count);


// ===== MAGNETIC BUTTONS =====
class MagneticButton {
  constructor(element, strength = 0.3) {
    this.element = element;
    this.strength = strength;
    this.rect = element.getBoundingClientRect();

    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseLeave = this.onMouseLeave.bind(this);

    this.init();
  }

  init() {
    this.element.addEventListener('mouseenter', () => {
      this.rect = this.element.getBoundingClientRect();
    });

    this.element.addEventListener('mousemove', this.boundMouseMove);
    this.element.addEventListener('mouseleave', this.boundMouseLeave);
  }

  onMouseMove(e) {
    const centerX = this.rect.left + this.rect.width / 2;
    const centerY = this.rect.top + this.rect.height / 2;

    const deltaX = (e.clientX - centerX) * this.strength;
    const deltaY = (e.clientY - centerY) * this.strength;

    this.element.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(1.05)`;
  }

  onMouseLeave() {
    this.element.style.transform = 'translate(0, 0) scale(1)';
  }

  destroy() {
    this.element.removeEventListener('mousemove', this.boundMouseMove);
    this.element.removeEventListener('mouseleave', this.boundMouseLeave);
  }
}

function initMagneticButtons() {
  // Skip magnetic effect on mobile devices for better performance
  if (window.innerWidth <= 768) return;

  const buttons = document.querySelectorAll('.btn-hero-primary, .btn-content-primary');
  buttons.forEach(btn => {
    if (!btn.dataset.magnetic) {
      new MagneticButton(btn, 0.2);
      btn.dataset.magnetic = 'true';
    }
  });
}


// ===== ANIMATED NUMBER COUNTER =====
function animateCounter(element, target, duration = 2000) {
  const start = 0;
  const increment = target / (duration / 16);
  let current = start;

  const timer = setInterval(() => {
    current += increment;
    if (current >= target) {
      element.textContent = Math.floor(target).toLocaleString();
      clearInterval(timer);
    } else {
      element.textContent = Math.floor(current).toLocaleString();
    }
  }, 16);
}

function initCounters() {
  const counters = document.querySelectorAll('[data-counter]');

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !entry.target.dataset.counted) {
        const target = parseInt(entry.target.dataset.counter);
        animateCounter(entry.target, target);
        entry.target.dataset.counted = 'true';
      }
    });
  }, { threshold: 0.5 });

  counters.forEach(counter => observer.observe(counter));
}


// ===== ENHANCED ADD TO CART =====
// (Removed — was firing optimistic confetti+toast on click BEFORE the actual
// /cart/add.js request completed, leading to a duplicate toast and confetti
// when global.js fired its own success feedback after the fetch resolved.
// global.js now owns all post-add feedback so it only fires on actual success.)
function enhanceAddToCart() {}

const productDataCache = {};

async function fetchProductData(handle) {
  if (!handle) return null;

  if (productDataCache[handle]) {
    return productDataCache[handle];
  }

  const response = await fetch(`/products/${handle}.js`);
  if (!response.ok) {
    throw new Error('Unable to load product data');
  }

  const data = await response.json();
  productDataCache[handle] = data;
  return data;
}

async function refreshCartCount() {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return;

    const cart = await response.json();
    const itemCount = window.ShoogieGolf?.cart?.getDisplayItemCount
      ? window.ShoogieGolf.cart.getDisplayItemCount(cart)
      : cart.item_count;
    let cartBadge = document.querySelector('.cart-badge');
    if (!cartBadge && itemCount > 0) {
      const cartIcon = document.querySelector('.cart-icon');
      if (cartIcon) {
        cartBadge = document.createElement('span');
        cartBadge.className = 'cart-badge';
        cartIcon.appendChild(cartBadge);
      }
    }

    if (cartBadge) {
      if (itemCount > 0) {
        cartBadge.textContent = itemCount;
        cartBadge.classList.add('is-visible');
      } else {
        cartBadge.remove();
      }
    }

    const cartCount = document.querySelector('[data-cart-count]');
    if (cartCount) {
      cartCount.textContent = itemCount;
    }
  } catch (error) {
    console.warn('Unable to update cart count', error);
  }
}
window.refreshCartCount = refreshCartCount;

function initQuickAddButtons() {
  if (window.__quickAddInitialized) return;
  window.__quickAddInitialized = true;

  if (!document.getElementById('quick-add-spinner-style')) {
    const style = document.createElement('style');
    style.id = 'quick-add-spinner-style';
    style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

  // NOTE: Quick Add button handler moved to global.js to consolidate cart functionality
  // This prevents duplicate event handlers and 429 rate limit errors
  // The global.js handler uses ShoogieGolf.cart.addItem() which properly:
  // - Updates cart count
  // - Refreshes cart drawer
  // - Triggers confetti and toast
  // - Opens cart drawer
  //
  // If you need to customize Quick Add behavior, edit the handler in global.js
}

// ===== SMOOTH SCROLL WITH OFFSET =====
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        const offset = 80; // Header height
        const targetPosition = target.offsetTop - offset;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  });
}


// ===== SCROLL REVEAL ANIMATIONS =====
function initScrollReveal() {
  const elements = document.querySelectorAll('.feature-card-modern, .testimonial-card-modern, .product-card-modern');

  // Reduce stagger delay on mobile
  const isMobile = window.innerWidth <= 768;
  const staggerDelay = isMobile ? 50 : 100;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }, index * staggerDelay);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  elements.forEach(el => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
    observer.observe(el);
  });
}


// ===== INITIALIZE EVERYTHING =====
document.addEventListener('DOMContentLoaded', () => {
  initMagneticButtons();
  initCounters();
  enhanceAddToCart();
  initQuickAddButtons();
  initSmoothScroll();
  initScrollReveal();

  console.log('Micro-interactions initialized.');
});

// Re-initialize on dynamic content load
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', () => {
    initMagneticButtons();
    initCounters();
  });
}
